import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Gift, Check, X, DollarSign, Mail, Phone, Filter, Trash2, MessageCircle, FileText, Send, Settings, Pencil } from "lucide-react";
import AdminLayout from "./AdminLayout";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = [
  { key: "all", label: "All" },
  { key: "pending", label: "Pending" },
  { key: "paid", label: "Paid" },
  { key: "cancelled", label: "Cancelled" },
];

const PAYOUT_METHODS = [
  { value: "etransfer", label: "Interac e-Transfer" },
  { value: "cash", label: "Cash at event" },
  { value: "refund", label: "Stripe partial refund" },
  { value: "credit", label: "Future-event credit" },
  { value: "other", label: "Other" },
];

const AdminMarketCampReferrals = () => {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [actionFor, setActionFor] = useState(null); // {credit, mode: "paid"|"cancelled"}
  const [actionMethod, setActionMethod] = useState("etransfer");
  const [actionNote, setActionNote] = useState("");

  const fetchData = async () => {
    setLoading(true);
    try {
      const url = filter === "all"
        ? `${API}/admin/market-camp/referrals`
        : `${API}/admin/market-camp/referrals?status=${filter}`;
      const res = await axios.get(url);
      setItems(res.data?.items || []);
      setSummary(res.data?.summary || {});
    } catch (e) {
      toast.error("Failed to load referrals");
    } finally {
      setLoading(false);
    }
  };

  const editAmount = async (credit) => {
    const current = (credit.amount || 0).toString();
    const input = window.prompt(
      `Edit credit amount for ${credit.referrer_name}.\nCurrent: $${current} ${(credit.currency || "cad").toUpperCase()}.\n\nEnter new amount in dollars (e.g. 30 for $30):`,
      current,
    );
    if (input === null) return;
    const amt = parseFloat(input);
    if (Number.isNaN(amt) || amt < 0) {
      toast.error("Invalid amount");
      return;
    }
    try {
      await axios.patch(`${API}/admin/market-camp/referrals/${credit.id}`, { amount: amt });
      toast.success(`Amount updated to $${amt.toFixed(2)}`);
      fetchData();
    } catch (e) {
      toast.error("Could not update amount");
    }
  };

  useEffect(() => { fetchData(); }, [filter]);

  const submitAction = async () => {
    if (!actionFor) return;
    const { credit, mode } = actionFor;
    try {
      await axios.patch(`${API}/admin/market-camp/referrals/${credit.id}`, {
        status: mode,
        paid_method: mode === "paid" ? actionMethod : "",
        paid_note: actionNote,
      });
      toast.success(mode === "paid" ? "Marked as paid" : "Marked as cancelled");
      setActionFor(null);
      setActionNote("");
      setActionMethod("etransfer");
      fetchData();
    } catch (e) {
      toast.error("Could not update credit");
    }
  };

  const deleteCredit = async (id) => {
    if (!window.confirm("Delete this credit record permanently?")) return;
    try {
      await axios.delete(`${API}/admin/market-camp/referrals/${id}`);
      toast.success("Deleted");
      fetchData();
    } catch (e) {
      toast.error("Could not delete");
    }
  };

  const resendEmail = async (id) => {
    try {
      await axios.post(`${API}/admin/market-camp/referrals/${id}/resend-email`);
      toast.success("Voucher email re-sent to referrer");
    } catch (e) {
      toast.error("Could not resend email");
    }
  };

  const sendPaidReceipt = async (id) => {
    try {
      await axios.post(`${API}/admin/market-camp/referrals/${id}/send-paid-receipt`);
      toast.success("Paid receipt email sent to referrer");
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send paid receipt");
    }
  };

  const voucherUrl = (id) => `${process.env.REACT_APP_BACKEND_URL}/api/public/referral-voucher/${id}`;

  const filteredItems = useMemo(() => items, [items]);

  return (
    <AdminLayout>
      <div className="p-6 lg:p-8 text-[#F5F5F0]" data-testid="admin-market-camp-referrals-page">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-black flex items-center gap-2">
              <Gift className="w-7 h-7 text-[#D4AF37]" />
              Market Camp · Referral Credits
            </h1>
            <p className="text-[#A1A1AA] text-sm mt-1">
              Each entry below is a thank-you credit owed to a parent or ambassador who referred a friend.
              Pay them out via Interac e-Transfer (or your preferred method) and mark as paid.
            </p>
          </div>
          <Link
            to="/admin/market-camp-settings"
            className="px-4 py-2 rounded-lg bg-[#27272A] text-[#D4AF37] font-bold inline-flex items-center gap-2 hover:bg-[#3F3F46]"
            data-testid="open-referral-settings"
          >
            <Settings className="w-4 h-4" /> Referral Settings
          </Link>
        </div>

        {/* Quick-info bar */}
        <div className="mb-5 rounded-xl p-3 bg-[rgba(212,175,55,0.06)] border border-[rgba(212,175,55,0.18)] text-[12px] text-[#A1A1AA]">
          <p>
            Default per-referral credit, payout method, share text, and on/off toggle live in
            <Link to="/admin/market-camp-settings" className="text-[#D4AF37] font-bold mx-1 underline">Market Camp Settings → Refer-a-Friend</Link>.
            To set a custom amount per partner, use
            <Link to="/admin/market-camp-ambassadors" className="text-[#D4AF37] font-bold mx-1 underline">Camp Ambassadors</Link>.
            You can also override the amount on any individual credit row below using the pencil icon.
          </p>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <SummaryCard label="Pending" value={summary.pending_count || 0} accent="#D4AF37" />
          <SummaryCard label="Pending $" value={`$${(summary.pending_amount || 0).toFixed(2)}`} accent="#D4AF37" />
          <SummaryCard label="Paid" value={summary.paid_count || 0} accent="#3FA437" />
          <SummaryCard label="Paid $" value={`$${(summary.paid_amount || 0).toFixed(2)}`} accent="#3FA437" />
        </div>

        {/* Filter tabs */}
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <Filter className="w-4 h-4 text-[#A1A1AA]" />
          {STATUSES.map((s) => (
            <button
              key={s.key}
              onClick={() => setFilter(s.key)}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${
                filter === s.key
                  ? "bg-[#D4AF37] text-[#050505]"
                  : "bg-[#1A1A1A] text-[#A1A1AA] hover:bg-[#27272A]"
              }`}
              data-testid={`filter-${s.key}`}
            >
              {s.label}
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="bg-[#0A0A0A] rounded-2xl border border-[rgba(212,175,55,0.15)] overflow-hidden">
          {loading ? (
            <div className="text-center py-12 text-[#A1A1AA]">Loading…</div>
          ) : filteredItems.length === 0 ? (
            <div className="text-center py-16">
              <Gift className="w-10 h-10 text-[#52525B] mx-auto mb-3" />
              <p className="text-[#A1A1AA]">No referral credits yet.</p>
              <p className="text-[#52525B] text-xs mt-1">Credits appear here once a friend redeems someone&apos;s share code at paid checkout.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-[#A1A1AA] border-b border-[rgba(212,175,55,0.15)]">
                    <th className="text-left px-3 py-3">Referrer</th>
                    <th className="text-left px-3 py-3">Friend</th>
                    <th className="text-left px-3 py-3">Amount</th>
                    <th className="text-left px-3 py-3">Status</th>
                    <th className="text-left px-3 py-3">Created</th>
                    <th className="text-left px-3 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredItems.map((c) => (
                    <tr key={c.id} className="border-b border-[rgba(212,175,55,0.08)] hover:bg-[rgba(212,175,55,0.04)]" data-testid={`credit-row-${c.id}`}>
                      <td className="px-3 py-3 align-top">
                        <p className="text-[#F5F5F0] font-bold">{c.referrer_name || "—"}</p>
                        <p className="text-[11px] text-[#A1A1AA] flex items-center gap-1 mt-0.5">
                          <Mail className="w-3 h-3" /> <span className="truncate max-w-[180px]">{c.referrer_email || "—"}</span>
                        </p>
                        <p className="text-[11px] text-[#A1A1AA] flex items-center gap-1 mt-0.5">
                          <Phone className="w-3 h-3" /> {c.referrer_phone || "—"}
                        </p>
                        <p className="text-[10px] text-[#52525B] font-mono mt-0.5">code {c.referrer_share_code}</p>
                      </td>
                      <td className="px-3 py-3 align-top">
                        <p className="text-[#F5F5F0]">{c.friend_name || "—"}</p>
                        <p className="text-[10px] text-[#52525B] font-mono mt-0.5">{c.friend_registration_code}</p>
                      </td>
                      <td className="px-3 py-3 align-top">
                        <div className="flex items-center gap-2">
                          <div>
                            <p className="font-extrabold text-[#D4AF37] text-base">${c.amount?.toFixed(2)}</p>
                            <p className="text-[10px] text-[#A1A1AA] uppercase">{c.currency}</p>
                          </div>
                          <button
                            onClick={() => editAmount(c)}
                            className="p-1 rounded hover:bg-[#D4AF37]/15 text-[#A1A1AA] hover:text-[#D4AF37]"
                            title="Edit amount"
                            data-testid={`edit-amount-${c.id}`}
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                      <td className="px-3 py-3 align-top">
                        <StatusPill status={c.status} />
                        {c.paid_method && (
                          <p className="text-[10px] text-[#A1A1AA] mt-1">via {c.paid_method}</p>
                        )}
                        {c.paid_note && (
                          <p className="text-[10px] text-[#52525B] italic mt-0.5 max-w-[180px] truncate" title={c.paid_note}>{c.paid_note}</p>
                        )}
                      </td>
                      <td className="px-3 py-3 align-top text-[11px] text-[#A1A1AA]">
                        {c.created_at ? new Date(c.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—"}
                        {c.paid_at && (
                          <p className="text-[10px] text-[#3FA437] mt-0.5">paid {new Date(c.paid_at).toLocaleDateString()}</p>
                        )}
                      </td>
                      <td className="px-3 py-3 align-top">
                        <div className="flex flex-col gap-1.5 min-w-[140px]">
                          {/* View / Print Voucher (always available) */}
                          <a
                            href={voucherUrl(c.id)}
                            target="_blank" rel="noopener noreferrer"
                            className="px-2.5 py-1.5 rounded-md text-[11px] font-bold bg-[#D4AF37]/15 text-[#D4AF37] hover:bg-[#D4AF37]/25 inline-flex items-center justify-center gap-1"
                            data-testid={`view-voucher-${c.id}`}
                          >
                            <FileText className="w-3.5 h-3.5" /> View Voucher
                          </a>
                          {/* Resend voucher email */}
                          {c.referrer_email && c.status !== "paid" && (
                            <button
                              onClick={() => resendEmail(c.id)}
                              className="px-2.5 py-1.5 rounded-md text-[11px] font-bold bg-blue-500/15 text-blue-400 hover:bg-blue-500/25 inline-flex items-center justify-center gap-1"
                              data-testid={`resend-${c.id}`}
                            >
                              <Send className="w-3.5 h-3.5" /> Resend Email
                            </button>
                          )}
                          {/* Send PAID receipt — only after status is paid */}
                          {c.referrer_email && c.status === "paid" && (
                            <button
                              onClick={() => sendPaidReceipt(c.id)}
                              className="px-2.5 py-1.5 rounded-md text-[11px] font-bold bg-green-500/15 text-green-400 hover:bg-green-500/25 inline-flex items-center justify-center gap-1"
                              data-testid={`send-paid-receipt-${c.id}`}
                              title="Email a PAID receipt voucher (with green PAID ✓ stamp)"
                            >
                              <DollarSign className="w-3.5 h-3.5" /> Email PAID Receipt
                            </button>
                          )}
                          {c.status === "pending" && (
                            <>
                              <button
                                onClick={() => { setActionFor({ credit: c, mode: "paid" }); setActionMethod("etransfer"); setActionNote(""); }}
                                className="px-2.5 py-1.5 rounded-md text-[11px] font-bold bg-green-500/15 text-green-400 hover:bg-green-500/25 inline-flex items-center justify-center gap-1"
                                data-testid={`mark-paid-${c.id}`}
                              >
                                <Check className="w-3.5 h-3.5" /> Mark Paid
                              </button>
                              <button
                                onClick={() => { setActionFor({ credit: c, mode: "cancelled" }); setActionNote(""); }}
                                className="px-2.5 py-1.5 rounded-md text-[11px] font-bold bg-red-500/15 text-red-400 hover:bg-red-500/25 inline-flex items-center justify-center gap-1"
                                data-testid={`cancel-${c.id}`}
                              >
                                <X className="w-3.5 h-3.5" /> Cancel
                              </button>
                            </>
                          )}
                          {c.status === "paid" && (
                            <button
                              onClick={() => { setActionFor({ credit: c, mode: "cancelled" }); setActionNote("Voided after paid status — please record reason"); }}
                              className="px-2.5 py-1.5 rounded-md text-[11px] font-bold bg-red-500/15 text-red-400 hover:bg-red-500/25 inline-flex items-center justify-center gap-1"
                              data-testid={`void-paid-${c.id}`}
                              title="Reverse this credit (e.g. payout failed)"
                            >
                              <X className="w-3.5 h-3.5" /> Void Credit
                            </button>
                          )}
                          {c.referrer_phone && (
                            <a
                              href={`https://wa.me/${(c.referrer_phone || "").replace(/[^\d]/g, "")}?text=${encodeURIComponent(`Hi ${c.referrer_name?.split(" ")[0] || "there"}, your $${c.amount} Mini Makers referral credit is ready. We'll send it via e-Transfer shortly. Thank you for sharing!`)}`}
                              target="_blank" rel="noopener noreferrer"
                              className="px-2.5 py-1.5 rounded-md text-[11px] font-bold bg-[#25D366]/15 text-[#25D366] hover:bg-[#25D366]/25 inline-flex items-center justify-center gap-1"
                              title="Open WhatsApp"
                            >
                              <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                            </a>
                          )}
                          <button
                            onClick={() => deleteCredit(c.id)}
                            className="px-2.5 py-1.5 rounded-md text-[11px] font-bold bg-[#27272A] text-[#A1A1AA] hover:bg-[#3F3F46] inline-flex items-center justify-center gap-1"
                            title="Delete"
                            data-testid={`delete-${c.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Action Modal */}
        {actionFor && (
          <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setActionFor(null)}>
            <div className="bg-[#0F0F0F] border border-[rgba(212,175,55,0.3)] rounded-2xl p-5 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-lg font-black text-[#F5F5F0] mb-1 flex items-center gap-2">
                {actionFor.mode === "paid" ? (
                  <><DollarSign className="w-5 h-5 text-[#D4AF37]" /> Mark as Paid</>
                ) : (
                  <><X className="w-5 h-5 text-red-400" /> Cancel Credit</>
                )}
              </h3>
              <p className="text-[#A1A1AA] text-xs mb-4">
                Referrer: <strong className="text-[#F5F5F0]">{actionFor.credit.referrer_name}</strong> · ${actionFor.credit.amount?.toFixed(2)}
              </p>
              {actionFor.mode === "paid" && (
                <div className="mb-3">
                  <label className="block text-[10px] uppercase tracking-wider text-[#D4AF37] font-bold mb-1.5">Payout Method</label>
                  <select
                    value={actionMethod}
                    onChange={(e) => setActionMethod(e.target.value)}
                    className="w-full px-3 py-2.5 rounded-lg bg-[#0A0A0A] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] focus:border-[#D4AF37] outline-none text-sm"
                    data-testid="action-method-select"
                  >
                    {PAYOUT_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                  </select>
                </div>
              )}
              <div className="mb-4">
                <label className="block text-[10px] uppercase tracking-wider text-[#D4AF37] font-bold mb-1.5">Note (optional)</label>
                <textarea
                  rows={3}
                  value={actionNote}
                  onChange={(e) => setActionNote(e.target.value)}
                  placeholder={actionFor.mode === "paid" ? "e.g. Sent via Interac on Aug 16, ref# 12345" : "Reason for cancellation"}
                  className="w-full px-3 py-2.5 rounded-lg bg-[#0A0A0A] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] focus:border-[#D4AF37] outline-none text-sm resize-none"
                  data-testid="action-note-input"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setActionFor(null)}
                  className="flex-1 px-4 py-2.5 rounded-lg bg-[#27272A] text-[#F5F5F0] font-bold hover:bg-[#3F3F46]"
                  data-testid="action-cancel"
                >
                  Back
                </button>
                <button
                  onClick={submitAction}
                  className={`flex-1 px-4 py-2.5 rounded-lg font-bold ${actionFor.mode === "paid" ? "bg-[#D4AF37] text-[#050505]" : "bg-red-500 text-white"}`}
                  data-testid="action-confirm"
                >
                  {actionFor.mode === "paid" ? "Confirm Paid" : "Confirm Cancel"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

const SummaryCard = ({ label, value, accent }) => (
  <div className="bg-[#0A0A0A] border border-[rgba(212,175,55,0.15)] rounded-xl p-4">
    <p className="text-[10px] uppercase tracking-wider font-bold" style={{ color: accent }}>{label}</p>
    <p className="text-2xl font-black text-[#F5F5F0] mt-1">{value}</p>
  </div>
);

const StatusPill = ({ status }) => {
  const styles = {
    pending: { bg: "rgba(212,175,55,0.15)", color: "#D4AF37", label: "Pending" },
    paid: { bg: "rgba(63,164,55,0.15)", color: "#3FA437", label: "Paid" },
    cancelled: { bg: "rgba(239,68,68,0.15)", color: "#EF4444", label: "Cancelled" },
  };
  const s = styles[status] || styles.pending;
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-wider" style={{ background: s.bg, color: s.color }}>
      {s.label}
    </span>
  );
};

export default AdminMarketCampReferrals;
